import { StaffAccessNotice } from "@/components/admin/StaffAccessNotice";
import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { Suspense } from "react";

import { SiteHeader } from "@/components/site/SiteHeader";
import { AdminNav } from "@/components/admin/AdminNav";
import { Skeleton } from "@/components/ui/skeleton";
import { getErrorReport } from "@/lib/admin-customers.functions";

const q = queryOptions({
  queryKey: ["admin", "errors"],
  queryFn: () => getErrorReport(),
  staleTime: 30_000,
});

export const Route = createFileRoute("/_authenticated/admin/errors")({
  head: () => ({ meta: [{ title: "Errores — Admin" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: ErrorsPage,
  errorComponent: ({ error }) => <main className="mx-auto max-w-3xl px-4 py-10"><StaffAccessNotice error={error} /></main>,
});

function ErrorsPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <h1 className="font-display text-4xl font-semibold">Errores e incidencias</h1>
        <p className="mt-1 text-sm text-muted-foreground">Reservas fallidas o canceladas que requieren atención.</p>
        <div className="mt-6"><AdminNav /></div>
        <Suspense fallback={<Skeleton className="mt-8 h-96 w-full" />}>
          <Body />
        </Suspense>
      </div>
    </div>
  );
}

function Body() {
  const { data } = useSuspenseQuery(q);
  if (data.failures.length === 0) {
    return (
      <div className="mt-8 rounded-2xl border border-dashed p-10 text-center">
        <p className="font-display text-xl">Sin incidencias</p>
        <p className="mt-1 text-sm text-muted-foreground">No hay reservas fallidas ni canceladas recientes.</p>
      </div>
    );
  }
  return (
    <div className="mt-8 overflow-hidden rounded-2xl border bg-card">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
          <tr>
            <th className="px-4 py-3">Código</th>
            <th className="px-4 py-3">Tour</th>
            <th className="px-4 py-3">Cliente</th>
            <th className="px-4 py-3">Estado</th>
            <th className="px-4 py-3">Origen</th>
            <th className="px-4 py-3">Fecha</th>
          </tr>
        </thead>
        <tbody>
          {data.failures.map((f: any) => (
            <tr key={f.id} className="border-t">
              <td className="px-4 py-3 font-mono text-xs">
                <Link to="/booking/$code" params={{ code: f.booking_code }} className="text-primary hover:underline">
                  {f.booking_code}
                </Link>
              </td>
              <td className="px-4 py-3">{f.tour_title}</td>
              <td className="px-4 py-3 text-muted-foreground">{f.customer_email}</td>
              <td className="px-4 py-3">
                <span className="rounded-full bg-destructive/15 px-2 py-0.5 text-xs font-medium text-destructive">
                  {f.status}
                </span>
              </td>
              <td className="px-4 py-3 text-muted-foreground">{f.source ?? "—"}</td>
              <td className="px-4 py-3 text-muted-foreground">{new Date(f.created_at).toLocaleString("es-MX")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
